import { dataService } from '../apis/dataService';
import type { UserDataContext } from '../../types/api';
import { isUnitScopeToggleAvailable } from './unitScopeRiskFiltering';

function isValidScopeUserId(n: number | null | undefined): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

/**
 * Risk Toggle Service
 * Handles the logic for toggling between "All Risks" and "My Units Only" views
 */
export class RiskToggleService {
  /**
   * Fetch risks for map scope using a concrete client user id (e.g. FilterContext selected client).
   * When userIdNo is missing, returns all risks (same as unauthenticated / no client).
   */
  static async fetchRisksForScope(
    userIdNo: number | null | undefined,
    showAllRisks: boolean
  ): Promise<Risk[]> {
    if (!isValidScopeUserId(userIdNo)) {
      return await dataService.getRisks({ filter_by_units: false });
    }
    try {
      const riskParams = showAllRisks
        ? { filter_by_units: false }
        : { user_id_no: userIdNo, filter_by_units: true };
      return await dataService.getRisks(riskParams);
    } catch (err) {
      console.error('Error fetching risks:', err);
      throw err;
    }
  }

  /**
   * Fetch risks based on toggle state
   * @param userDataContext - Current user data context
   * @param showAllRisks - Whether to show all risks (true) or filter by units (false)
   * @returns Promise<Risk[]> - Array of risk data
   */
  static async fetchRisks(
    userDataContext: UserDataContext | null, 
    showAllRisks: boolean
  ): Promise<Risk[]> {
    if (!userDataContext) {
      console.warn('No user data context available for risk fetching');
      return [];
    }
    return this.fetchRisksForScope(userDataContext.user_id_no, showAllRisks);
  }

  /**
   * Get the display text for the current toggle state
   * @param showAllRisks - Current toggle state
   * @returns string - Display text
   */
  static getToggleDisplayText(showAllRisks: boolean): string {
    return showAllRisks ? 'All Risks' : 'My Units Only';
  }

  /**
   * Get the risk count display text
   * @param riskCount - Number of risks
   * @param showAllRisks - Current toggle state
   * @returns string - Formatted count text
   */
  static getRiskCountText(riskCount: number, showAllRisks: boolean): string {
    const filterText = this.getToggleDisplayText(showAllRisks);
    return `${riskCount} total - ${filterText}`;
  }

  /**
   * Validate if risk toggle is available for the current user context
   * @param userDataContext - Current user data context
   * @returns boolean - Whether toggle is available
   */
  static isToggleAvailable(userDataContext: UserDataContext | null): boolean {
    return (
      userDataContext != null && isUnitScopeToggleAvailable(userDataContext.user_id_no)
    );
  }
}

// Export a default instance for convenience
export const riskToggleService = new RiskToggleService();